import {
  AssetCountRow,
  AssetCountTotals,
  NozomiVantageSensorAssetCount,
} from '@totem/components/administration/reconciliation/nozomi/vantage/types';

const EMPTY_TOTALS: AssetCountTotals = {
  total: 0,
  active24h: 0,
  active7d: 0,
  active30d: 0,
  it: 0,
  ot: 0,
  iot: 0,
  otherCategory: 0,
  noMac: 0,
  localMacOnly: 0,
  counted: 0,
};

// A sensor that failed its last attempt still shows its last successful numbers (countedAt is set).
const hasCount = (sensor: NozomiVantageSensorAssetCount) =>
  !!sensor.count &&
  !!sensor.count.countedAt &&
  !sensor.count.countedAt.startsWith('0001');

const sensorTotals = (
  sensor: NozomiVantageSensorAssetCount,
): AssetCountTotals => {
  if (!hasCount(sensor)) {
    return { ...EMPTY_TOTALS };
  }

  const { count } = sensor;
  return {
    total: count.total,
    active24h: count.active24h,
    active7d: count.active7d,
    active30d: count.active30d,
    it: count.it,
    ot: count.ot,
    iot: count.iot,
    otherCategory: count.otherCategory,
    noMac: count.noMac,
    localMacOnly: count.localMacOnly,
    counted: 1,
  };
};

export const sumTotals = (items: AssetCountTotals[]): AssetCountTotals =>
  items.reduce(
    (sum, item) => ({
      total: sum.total + item.total,
      active24h: sum.active24h + item.active24h,
      active7d: sum.active7d + item.active7d,
      active30d: sum.active30d + item.active30d,
      it: sum.it + item.it,
      ot: sum.ot + item.ot,
      iot: sum.iot + item.iot,
      otherCategory: sum.otherCategory + item.otherCategory,
      noMac: sum.noMac + item.noMac,
      localMacOnly: sum.localMacOnly + item.localMacOnly,
      counted: sum.counted + item.counted,
    }),
    { ...EMPTY_TOTALS },
  );

export const getTotals = (sensors: NozomiVantageSensorAssetCount[]) =>
  sumTotals(sensors.map(sensorTotals));

export const isCountFailed = (sensor: {
  count?: { success: boolean; lastAttemptAt: string } | null;
}) => !!sensor.count && !sensor.count.success;

export const buildRows = (
  sensors: NozomiVantageSensorAssetCount[],
): AssetCountRow[] => {
  const byOrganization = new Map<string, NozomiVantageSensorAssetCount[]>();
  sensors.forEach((sensor) => {
    const orgKey = sensor.organization.id || sensor.organization.name;
    byOrganization.set(orgKey, [...(byOrganization.get(orgKey) ?? []), sensor]);
  });

  const rows: AssetCountRow[] = [];
  byOrganization.forEach((orgSensors, orgKey) => {
    const children = orgSensors
      .map((sensor): AssetCountRow => ({
        ...sensorTotals(sensor),
        key: sensor.siteId,
        kind: 'sensor',
        name: sensor.siteName,
        siteId: sensor.siteId,
        building: sensor.building?.name || 'No building assigned',
        mapped: sensor.mapped,
        count: sensor.count,
      }))
      .sort((left, right) => right.total - left.total);

    rows.push({
      ...sumTotals(children),
      key: `org-${orgKey}`,
      kind: 'organization',
      name: orgSensors[0].organization.name || 'No organization',
      sensorCount: orgSensors.length,
      children,
    });
  });

  return rows.sort((left, right) => right.total - left.total);
};

// Oldest and newest successful count, to show how current the page is.
export const getCountedRange = (sensors: NozomiVantageSensorAssetCount[]) => {
  const times = sensors
    .filter(hasCount)
    .map((sensor) => new Date(sensor.count.countedAt).getTime())
    .sort((left, right) => left - right);

  return times.length === 0
    ? null
    : { oldest: new Date(times[0]), newest: new Date(times[times.length - 1]) };
};
